import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useItemTags, type TaggableItemType } from './useItemTags';
import { isReservedTag } from './types';
import './ItemTagActions.css';

interface ItemTagActionsProps {
  itemType: TaggableItemType;
  itemId: string;
  /** Hide tag chips, show only the add-tag button */
  compact?: boolean;
}

const POPOVER_WIDTH = 240;
const POPOVER_GAP = 6;

const PULSE_EVENT = 'qtag:pulse';
const PULSE_MS = 1600;

/**
 * Draw the eye to this item's tag control — used when the item was tagged from
 * somewhere else on the screen (the wrong-answer tag nudge), so the student
 * learns where tags live. A window event because the two sit in unrelated trees.
 */
export function pulseItemTagActions(itemType: TaggableItemType, itemId: string): void {
  window.dispatchEvent(new CustomEvent(PULSE_EVENT, { detail: { itemType, itemId } }));
}

export function ItemTagActions({ itemType, itemId, compact }: ItemTagActionsProps) {
  const { availableTags: rawAvailable, itemTags: rawItemTags, toggleTag, createAndAddTag } = useItemTags(itemType, itemId);
  // Hide reserved system tags (e.g. thumbs-up/down) from the regular tag UI — they
  // are surfaced via dedicated affordances like QuestionFeedbackActions.
  const availableTags = useMemo(() => rawAvailable.filter(t => !isReservedTag(t)), [rawAvailable]);
  const itemTags = useMemo(() => rawItemTags.filter(t => !isReservedTag(t)), [rawItemTags]);
  const [showPopover, setShowPopover] = useState(false);
  const [newTagInput, setNewTagInput] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  // Portal-rendered popover uses fixed coords so it can't be clipped by an
  // ancestor's `overflow: auto` (e.g. data-table containers in Bookmarks).
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

  const handleToggle = (tag: string) => {
    toggleTag(tag);
    setShowPopover(false);
  };

  const handleAddCustomTag = () => {
    const trimmed = newTagInput.trim();
    if (!trimmed) return;
    createAndAddTag(trimmed);
    setNewTagInput('');
    setShowPopover(false);
  };

  const computePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const top = rect.bottom + POPOVER_GAP;
    // Right-align by default (popover's right edge under button's right edge),
    // but flip to left-align if it would clip the viewport's left side.
    let left = rect.right - POPOVER_WIDTH;
    if (left < 8) left = rect.left;
    // Clamp to viewport on the right too.
    const maxLeft = window.innerWidth - POPOVER_WIDTH - 8;
    if (left > maxLeft) left = Math.max(8, maxLeft);
    setPopoverPos({ top, left });
  }, []);

  const openPopover = useCallback(() => {
    setShowPopover(v => {
      const next = !v;
      if (next) computePosition();
      return next;
    });
  }, [computePosition]);

  useEffect(() => {
    if (!showPopover) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideContainer = containerRef.current?.contains(target);
      const insidePopover = popoverRef.current?.contains(target);
      if (!insideContainer && !insidePopover) setShowPopover(false);
    };
    const handleViewportChange = () => setShowPopover(false);
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [showPopover]);

  useEffect(() => {
    setShowPopover(false);
    setNewTagInput('');
  }, [itemId]);

  const [pulsing, setPulsing] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onPulse = (e: Event) => {
      const d = (e as CustomEvent<{ itemType: string; itemId: string }>).detail;
      if (d?.itemType !== itemType || d.itemId !== itemId) return;
      setPulsing(true);
      clearTimeout(timer);
      timer = setTimeout(() => setPulsing(false), PULSE_MS);
    };
    window.addEventListener(PULSE_EVENT, onPulse);
    return () => { window.removeEventListener(PULSE_EVENT, onPulse); clearTimeout(timer); };
  }, [itemType, itemId]);

  return (
    <div className={`qtag-row${pulsing ? ' qtag-row--pulse' : ''}`} ref={containerRef}>
      {!compact && itemTags.map(tag => (
        <span key={tag} className="qtag-chip">{tag}</span>
      ))}
      <button
        className="qtag-btn"
        ref={buttonRef}
        onClick={openPopover}
        aria-label={`Tag ${itemType}`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
        {itemTags.length === 0 && <span>Tag</span>}
      </button>
      {showPopover && popoverPos && createPortal(
        <div
          className="qtag-popover qtag-popover-portal"
          ref={popoverRef}
          style={{ top: popoverPos.top, left: popoverPos.left, width: POPOVER_WIDTH }}
        >
          <div className="qtag-popover-title">Tags</div>
          <div className="qtag-chips">
            {availableTags.map(tag => (
              <button
                key={tag}
                className={`qtag-chip-btn ${itemTags.includes(tag) ? 'active' : ''}`}
                onClick={() => handleToggle(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="qtag-add-row">
            <input
              type="text"
              value={newTagInput}
              onChange={e => setNewTagInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddCustomTag(); }}
              placeholder="New tag..."
              maxLength={30}
            />
            <button onClick={handleAddCustomTag} disabled={!newTagInput.trim()}>+</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
